import { memo, useMemo } from 'react';

import { useRewardSystemUser } from 'hooks/rewardSystem/useRewardSystemUser';

import { useGetSeasonsQuery } from 'store/api/strapiService/rewardSystemController';

import { getAchievedKPI, getCoinsAccrued } from 'utils/rewardSystem';

import Loader from 'components/UI/Loader/Loader';
import NoData from 'components/UI/NoData/NoData';
import Table from 'components/UI/Table/Table';
import Typography from 'components/UI/Typography/Typography';

import { KPI_KEYS } from 'components/config/rewardSystem';
import { SEASON_COLUMNS } from 'components/config/rewardSystem';

import styles from './Rewards.module.scss';

const Rewards = () => {
  const { companyId } = useRewardSystemUser();

  const { data: seasonsData, isLoading } = useGetSeasonsQuery(
    { companyId },
    { skip: !companyId },
  );

  const seasons = seasonsData?.data;

  const data = useMemo(
    () =>
      seasons?.map((season) => ({
        id: season.documentId,
        name: season.name,
        rows: KPI_KEYS.map((key) => {
          const [type, percentage] = key.split('KPI');
          const achieved = getAchievedKPI(season, type, percentage);
          const { isDone: accrued, value } = season[key];

          return {
            activity: (
              <Typography
                type="text-sm"
                weight="medium"
                color="grey"
                align="left"
              >
                Achieve {percentage}% of {type} KPI
              </Typography>
            ),
            reward: (
              <Typography
                type="text-sm"
                weight="medium"
                color={achieved ? 'blue' : 'grey'}
              >
                {value} coins
              </Typography>
            ),
            status: (
              <Typography
                type="text-sm"
                weight="medium"
                color={achieved ? 'green' : 'grey'}
              >
                {achieved ? 'Achieved' : 'Not Achieved'}
              </Typography>
            ),
            accrued: (
              <Typography
                type="text-sm"
                weight="medium"
                color={accrued ? 'green' : 'grey'}
              >
                {accrued ? 'Yes' : '-'}
              </Typography>
            ),
          };
        }),
        total: getCoinsAccrued(season),
      })),
    [seasons],
  );

  if (isLoading) return <Loader />;

  if (!seasons?.length)
    return <NoData description="Try to reload the page or contact support" />;

  return (
    <div className={styles.root}>
      {data?.map(({ id, name, rows, total }) => (
        <div key={id} className={styles.seasonWrapper}>
          <Typography type="text-lg" weight="medium" uppercase>
            {name}
          </Typography>
          <Table columns={SEASON_COLUMNS} rows={rows} />
          <Typography color="grey">
            Total earned:{' '}
            <Typography element="span" weight="semibold" color="blue">
              {total}
            </Typography>
          </Typography>
        </div>
      ))}
    </div>
  );
};

export default memo(Rewards);
